// components/drive/DriveListItem.tsx
import { FC, useMemo } from 'react';
import { Card, Button, Dropdown, Space, Typography, List } from 'antd';
import type { MenuProps } from 'antd';
import { FolderFilled, FileOutlined, MoreOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

export type ExtractionAction =
  | 'identity'
  | 'statement-basic'
  | 'credit'
  | 'mortgage'
  | 'payslip'
  | 'bank-ai'
  | 'employment'
  | 'tenancy'
  | 'ftc';

export interface DriveOwner {
  emailAddress?: string;
}

export interface DriveItem {
  id: string;
  name: string;
  mimeType: string;
  owners: DriveOwner[];
  modifiedTime?: string;
  webViewLink?: string;
}

interface DriveListItemProps {
  item: DriveItem;
  loading?: boolean;
  isFolderFetching?: boolean;
  onOpenFolder: (folderId: string) => void;
  onAction: (action: ExtractionAction, fileId: string, item: DriveItem) => void;
}

const DriveListItem: FC<DriveListItemProps> = ({
  item,
  loading,
  isFolderFetching,
  onOpenFolder,
  onAction,
}) => {
  const isFolder = item.mimeType === 'application/vnd.google-apps.folder';
  const isPdf = item.mimeType === 'application/pdf';

  const menuItems: MenuProps['items'] = useMemo(
    () => [
      { key: 'identity', label: 'Extract ID (ID Card, Passport, etc)' },
      { key: 'credit', label: 'Extract Credit Card Statement' },
      { key: 'mortgage', label: 'Extract Mortgage Statement' },
      { key: 'payslip', label: 'Extract Payslip' },
      { key: 'bank-ai', label: 'Extract Bank Statement (AI)' },
      { key: 'employment', label: 'Extract Employment Contract' },
      { key: 'tenancy', label: 'Extract Tenancy Agreement' },
      { key: 'ftc', label: 'Extract FTC Document' },
    ],
    [],
  );

  const menu: MenuProps = {
    items: menuItems,
    onClick: ({ key }) => onAction(key as ExtractionAction, item.id, item),
  };

  return (
    <List.Item>
      <Card
        style={{ width: '100%', maxWidth: '100%' }}
        size="small"
        loading={loading || isFolderFetching}
        onClick={() => {
          if (isFolder) onOpenFolder(item.id);
        }}
      >
        {isPdf && (
          <Dropdown menu={menu} trigger={['click']}>
            <Button
              type="text"
              style={{ position: 'absolute', right: 0, top: 0 }}
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        )}

        <Space direction="vertical" size="small">
          <Space align="start">
            {isFolder ? <FolderFilled /> : <FileOutlined />}
            <Text style={{ fontWeight: 700 }}>{item.name}</Text>
          </Space>

          {item.owners?.[0]?.emailAddress && (
            <Text type="secondary">Owner: {item.owners[0].emailAddress}</Text>
          )}

          {item.modifiedTime && (
            <Text type="secondary">
              Last Modified:{' '}
              {dayjs(item.modifiedTime).format('YYYY-MM-DD HH:mm')}
            </Text>
          )}

          {item.webViewLink && (
            <Space>
              <Button
                href={item.webViewLink}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
                type="link"
              >
                Open in Google Drive
              </Button>
            </Space>
          )}
        </Space>
      </Card>
    </List.Item>
  );
};

export default DriveListItem;
